/**
 * PaidDashboard — overview layout for Starter / Pro tiers. Surfaces:
 *   - Usage meters (projects + seats)
 *   - Upload zone (collapsible once recent reports exist)
 *   - Recent reports
 *   - Per-project score sparklines
 *   - Team link (for admins)
 */
import { useEffect, useState } from 'react';
import { api } from '../api/client.js';
import { UploadZone } from '../components/UploadZone.jsx';
import { RecentReportsTable } from '../components/RecentReportsTable.jsx';
import { UsageMeter } from '../components/UsageMeter.jsx';
import { ScoreSparkline } from '../components/ScoreSparkline.jsx';
import { UpgradeCTA } from '../components/UpgradeCTA.jsx';
import { formatOverallScore } from '../../shared/scoreDisplay.js';

export function PaidDashboard({ user, organization, navigate }) {
  const [usage, setUsage] = useState(null);
  const [recent, setRecent] = useState([]);
  const [trends, setTrends] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.getOrgUsage(), api.getRecentReports(), api.getScoreTrends()])
      .then(([u, r, t]) => {
        if (cancelled) return;
        setUsage(u);
        setRecent(Array.isArray(r?.reports) ? r.reports : []);
        setTrends(Array.isArray(t?.projects) ? t.projects : []);
      })
      .catch((e) => { if (!cancelled) setErr(e); });
    return () => { cancelled = true; };
  }, []);

  const isAdmin = user?.role === 'admin';
  const subStatus = organization?.subscription_status;
  const isReadOnlyAdmin =
    isAdmin && (subStatus === 'expired' || subStatus === 'read_only');
  const tierLabel = (organization?.subscription_tier || 'starter').toUpperCase();

  return (
    <main className="main-content dashboard-paid" role="main">
      <section className="dashboard-hero">
        <h1>
          Welcome back{user?.full_name ? `, ${user.full_name.split(' ')[0]}` : ''}.
        </h1>
        <p>
          {organization?.name || 'Your team'} — <strong>{tierLabel}</strong> plan
        </p>
      </section>

      {err && (
        <p className="recent-reports-error" role="alert">
          Couldn&rsquo;t load dashboard data. Try refreshing the page.
        </p>
      )}

      {usage && (
        <section className="dashboard-usage-grid" aria-label="Plan usage">
          <UsageMeter
            label="Projects"
            used={usage.projects_count}
            cap={usage.projects_cap}
            hint={usage.projects_cap ? `Up to ${usage.projects_cap} on ${tierLabel}` : 'Unlimited'}
          />
          <UsageMeter
            label="Team seats"
            used={usage.seat_count + usage.pending_invites_count}
            cap={usage.seat_cap}
            hint={
              usage.pending_invites_count > 0
                ? `${usage.seat_count} active · ${usage.pending_invites_count} pending`
                : null
            }
          />
          <div className="dashboard-usage-cta">
            {isAdmin && (
              <>
                <a
                  href="/team"
                  className="dashboard-link-card"
                  aria-disabled={isReadOnlyAdmin || undefined}
                  aria-describedby={isReadOnlyAdmin ? 'manage-team-disabled-reason' : undefined}
                  onClick={(e) => {
                    e.preventDefault();
                    if (isReadOnlyAdmin) return;
                    navigate('/team');
                  }}
                >
                  Manage team →
                </a>
                {isReadOnlyAdmin && (
                  <span id="manage-team-disabled-reason" className="sr-only">
                    Read-only mode. Renew to manage team.
                  </span>
                )}
              </>
            )}
            <UpgradeCTA label="Upgrade plan" navigate={navigate} />
          </div>
        </section>
      )}

      <section className="dashboard-upload-inline">
        <h2>Upload a new schedule</h2>
        <UploadZone
          variant="compact"
          onUploaded={(d) => navigate(`/reports/${d.report_id}`)}
          disabled={isReadOnlyAdmin}
          disabledReason={isReadOnlyAdmin ? 'Read-only mode. Renew to upload schedules.' : null}
        />
      </section>

      <section className="recent-reports">
        <h2>Recent reports</h2>
        <RecentReportsTable reports={recent} navigate={navigate} />
      </section>

      {trends.length > 0 && (
        <section className="score-trends">
          <h2>Project score trends</h2>
          <div className="score-trends-grid">
            {trends.map((proj) => {
              const last = proj.points[proj.points.length - 1];
              const rejected = last && last.verdict === 'REJECTED';
              const lastScore = last ? last.score : null;
              return (
                <div key={proj.project_name} className="score-trend-card">
                  <div className="score-trend-head">
                    <span className="score-trend-name">{proj.project_name}</span>
                    <span className="score-trend-score">
                      {/* FIX-B Part 3: the ONE display score (shared formatter). */}
                      {formatOverallScore(lastScore)}
                    </span>
                  </div>
                  <ScoreSparkline points={proj.points} rejected={rejected} />
                  <span className="score-trend-count">
                    {proj.points.length} report{proj.points.length === 1 ? '' : 's'}
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </main>
  );
}
